/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'helpers/store'
import { OffersAction } from 'store/offers/OffersAction'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import { CompanyContact } from 'types/entities/CompanyContact'
import { closeSecondaryModal } from 'helpers/actions'
import CompanyContactAutocomplete from 'components/CompanyContactAutocomplete'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import { toast } from 'utils/toastUtils'
import CompanyContactForm from 'components/Companies/forms/CompanyContactForm'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'

interface ChangeOfferContactModalProps {
    offerId: number
    sellerId: number
    companyId: number | null
    addressId?: number | null
    initialContact?: CompanyContact | null
    companyContacts?: CompanyContact[] | null
    onSuccess?: () => void
}

const ChangeOfferContactModal: React.FC<ChangeOfferContactModalProps> = ({
    offerId,
    sellerId,
    companyId,
    addressId,
    initialContact,
    companyContacts,
    onSuccess,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [selectedContact, setSelectedContact] =
        React.useState<CompanyContact | null>(initialContact || null)
    const [availableContacts, setAvailableContacts] = React.useState<CompanyContact[]>(companyContacts ?? [])
    const [isLoading, setIsLoading] = React.useState(false)
    const [isSaving, setIsSaving] = React.useState(false)
    const [showCreateContactForm, setShowCreateContactForm] = React.useState(false)

    React.useEffect(() => {
        if (!companyId) {
            setAvailableContacts([])
            return
        }

        if (companyContacts && companyContacts.length > 0) {
            setAvailableContacts(companyContacts)
        }

        setIsLoading(true)
        dispatch(CompaniesAction.fetchCompanyById(companyId)).then((company) => {
            setAvailableContacts(company?.contacts ?? companyContacts ?? [])
            setIsLoading(false)
        }).catch(() => {
            setAvailableContacts(companyContacts ?? [])
            setIsLoading(false)
        })
    }, [companyContacts, companyId, dispatch])

    const handleSave = async (shouldClose: boolean = false) => {
        if (!selectedContact || !companyId) return

        setIsSaving(true)
        try {
            await dispatch(
                OffersAction.updateOffer({
                    id: offerId,
                    seller_id: sellerId,
                    company_id: companyId,
                    contact_id: Number(selectedContact.id),
                    address_id: addressId ?? null,
                })
            )
            toast.success(t('Person updated successfully'))
            if (onSuccess) onSuccess()
            if (shouldClose) {
                dispatch(closeSecondaryModal())
            }
        } catch {
            toast.error(t('Failed to save person'))
        } finally {
            setIsSaving(false)
        }
    }

    const hasContactOptions = availableContacts.length > 0
    const shouldShowCreateContact = !isLoading && !!companyId && !hasContactOptions && !showCreateContactForm

    return (
        <UiFormModalShell
            className="ChangeOfferContactModal"
            title={t('Change Contact Person')}
            subtitle={t('Select a new contact person for this offer')}
            showHeader={true}
            footer={
                showCreateContactForm ? undefined : (
                    <UiFormActionsBar
                        className="ChangeOfferContactModalActions w-full px-6 py-4"
                        primaryText={t('Save') as string}
                        onPrimary={() => handleSave(false)}
                        saveAndCloseText={t('Save & Close') as string}
                        onSaveAndClose={() => handleSave(true)}
                        loading={isSaving}
                        disabled={!companyId || !selectedContact || selectedContact.id === initialContact?.id || isLoading}
                    />
                )
            }
        >
            <UiFormInner>
                <div className="ChangeOfferContactModalContent">
                    {showCreateContactForm ? (
                        <CompanyContactForm
                            inline={true}
                            showSaveAndClose={false}
                            onBack={() => setShowCreateContactForm(false)}
                            onChange={(contact) => {
                                setAvailableContacts((prev) => {
                                    const withoutSameContact = prev.filter((item) => item.id !== contact.id)
                                    return [...withoutSameContact, contact]
                                })
                                setSelectedContact(contact)
                                setShowCreateContactForm(false)
                            }}
                        />
                    ) : (
                        <>
                            <CompanyContactAutocomplete
                                label={t('Contact person')}
                                name="contact_id"
                                defaultValue={selectedContact}
                                initialContacts={availableContacts}
                                restrictToInitialContacts={true}
                                onSelect={(contact) => setSelectedContact(contact)}
                                placeholder={
                                    isLoading
                                        ? t('Loading...')
                                        : hasContactOptions
                                            ? t('Search for contact...')
                                            : t('No contact persons assigned')
                                }
                                disabled={isLoading || !hasContactOptions}
                            />
                            {shouldShowCreateContact && (
                                <div className="ChangeOfferContactModalCreateHint mt-2 flex items-center justify-between gap-2">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {t('No contact persons assigned')}
                                    </span>
                                    <UiButton
                                        title={t('Create new contact')}
                                        style={UiButtonStyle.Secondary}
                                        size={UiButtonSize.Small}
                                        onClick={() => setShowCreateContactForm(true)}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </UiFormInner>
        </UiFormModalShell>
    )
}

export default ChangeOfferContactModal
